import type { ClassValue } from "clsx";
import type { FieldValues } from "react-hook-form";
import { ShiftRepeat } from "@repo/database";
import { isBetween } from "@repo/utils";
import { clsx } from "clsx";
import { addMinutes, getDay, isSameDay, set } from "date-fns";
import { twMerge } from "tailwind-merge";

import type { Shift } from "~/providers/shift-provider";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractDirtyFields<T extends FieldValues>(
  data: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dirtyFields: Partial<Record<keyof T, boolean | Record<string, any>>>,
): Partial<T> {
  return Object.fromEntries(
    Object.entries(data).filter(([key, value]) => {
      const isDirty = dirtyFields[key as keyof T];
      if (
        typeof isDirty === "object" &&
        value !== null &&
        typeof value === "object"
      ) {
        // Handle nested or array fields
        return Object.keys(isDirty).length > 0;
      }
      return isDirty;
    }),
  ) as Partial<T>;
}

export function getMatchedShifts(shifts: Shift[], date: Date) {
  return shifts
    .filter((shift) => {
      if (shift.repeat === ShiftRepeat.never) {
        return isSameDay(shift.startDate, date);
      } else if (shift.repeat === ShiftRepeat.weekly) {
        if (shift.repeatDays.includes(getDay(date))) {
          return true;
        }
      }
    })
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
}

export function bookingWithinShift(shifts: Shift[], begin: Date) {
  return shifts
    .filter((shift) => {
      const bookingEnd = addMinutes(begin, shift.standardDuration);

      // For non-repeating shifts, check if the date matches and time is within range
      if (shift.repeat === ShiftRepeat.never) {
        if (!isSameDay(shift.startDate, begin)) return false;

        const shiftStart = set(shift.startDate, {
          hours: shift.startTime.getHours(),
          minutes: shift.startTime.getMinutes(),
          seconds: 0,
          milliseconds: 0,
        });

        const shiftEnd = addMinutes(
          set(shift.startDate, {
            hours: shift.endTime.getHours(),
            minutes: shift.endTime.getMinutes(),
            seconds: 0,
            milliseconds: 0,
          }),
          shift.standardDuration,
        );

        return (
          isBetween(begin, shiftStart, shiftEnd) &&
          isBetween(bookingEnd, shiftStart, shiftEnd)
        );
      }

      // For weekly repeating shifts, check if the day matches and time is within range
      if (shift.repeat === ShiftRepeat.weekly) {
        if (!shift.repeatDays.includes(getDay(begin))) return false;

        const shiftStart = set(begin, {
          hours: shift.startTime.getHours(),
          minutes: shift.startTime.getMinutes(),
          seconds: 0,
          milliseconds: 0,
        });

        const shiftEnd = addMinutes(
          set(begin, {
            hours: shift.endTime.getHours(),
            minutes: shift.endTime.getMinutes(),
            seconds: 0,
            milliseconds: 0,
          }),
          shift.standardDuration,
        );

        return (
          isBetween(begin, shiftStart, shiftEnd) &&
          isBetween(bookingEnd, shiftStart, shiftEnd)
        );
      }

      return false;
    })
    .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
}

export const fetchIpAddress = async (): Promise<string> => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const { ip } = (await response.json()) as { ip: string };
    return ip;
  } catch (error) {
    console.error("Error fetching IP address:", error);
    throw new Error("IP_FETCH_FAILED");
  }
};
